import { useBoolean } from 'ahooks';
import { Space } from 'antd';
import HighchartsReact from 'highcharts-react-official';
import Highcharts, { Options } from 'highcharts/highstock';
import { isEqual, keyBy } from 'lodash';
import React, { FC, memo, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Widget as WidgetT } from 'api/dashboard';
import {
  ChartOptions,
  CustomChartWidget,
  WidgetType,
  SimpleSearchFilter,
} from 'api/dashboard/widget';
import { Project } from 'api/project';
import { Loading } from 'components/atoms/loading';
import { Filter } from 'components/molecules/filters/types';
import { FieldDataType } from 'components/organisms/module-settings/fields/types';
import { QueryCombinerType } from 'components/organisms/tasks/combiner';
import { FilterGroupType } from 'components/pages/types';
import { HelperText01 } from 'components/styles';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useAllFieldsQuery } from 'queries';
import {
  useDrillWidgetMutationV2,
  useResolveMultipleWidgetsQuery,
} from 'queries/widgets';
import { useGetEditWidgetUrlWithSearch } from 'utils/urls';
import { useWidgetFilter } from '../use-widget-filter';
import { useWidgetMenu } from '../use-widget-menu';
import {
  fieldsOfChart,
  getFieldInfo,
  getProjectAndTaskFields,
  transformOptions,
  useChartOptionsWithSortAndDrillDown,
} from './chart-formatting-utils';
import { ChartWidgetContent } from './chart-widget-content';
import { TOGGLE_CHART_LABEL_LOCALSTORAGE_KEY } from './common';
import { chartCleanup, scrollIntialiser } from './plugin';
import { ObjectType } from './types';
import { getFilterForDrillWidget, isNativeDateField } from './utils';
import { ErrorState, NoResults } from './widget-state';

const LoadingMessage = styled.div`
  ${HelperText01};
`;

const InfoWrapper = styled.div`
  position: absolute;
  bottom: 0;
`;

const compareChartProps = (preProps, nextProps): boolean => {
  // react query makes unwanted re-renders - to prevent that
  const xAxisField =
    preProps?.options?.xAxis?.title?.text ===
    nextProps?.options?.xAxis?.title?.text;
  const yAxisField =
    preProps?.options?.yAxis?.title?.text ===
    nextProps?.options?.yAxis?.title?.text;
  const groupByField =
    preProps.options?.groupBy?.name === nextProps.options?.groupBy?.name;
  const chartType =
    preProps?.options?.chart?.type === nextProps?.options?.chart?.type;
  const filter = isEqual(preProps.filter, nextProps.filter);
  const objectType = preProps.objectType === nextProps.objectType;
  const sortType = preProps.sortType === nextProps.sortType;
  const zoomLevel = preProps.zoomLevel === nextProps.zoomLevel;
  const aggType = preProps.aggType === nextProps.aggType;

  return (
    xAxisField &&
    yAxisField &&
    groupByField &&
    chartType &&
    filter &&
    objectType &&
    sortType &&
    aggType &&
    zoomLevel
  );
};

const Chart = memo(HighchartsReact, compareChartProps);

const valuesChecker = (value) => {
  const val = value === 'object' ? value?.y : value;

  return (val ?? 0) === 0;
};

const handleEmpty = (chartOptions: ChartOptions | null): boolean => {
  if (chartOptions?.groupBy) {
    return (
      (chartOptions?.series ?? []).length > 0 &&
      !chartOptions?.series?.every((_series) =>
        _series.data?.every(valuesChecker),
      )
    );
  }

  return (
    (chartOptions?.series ?? []).length > 0 &&
    (chartOptions?.series?.[0]?.data ?? []).length > 0 &&
    !(chartOptions?.series?.[0]?.data ?? []).every(valuesChecker)
  );
};

export const getChartContent = (
  status: string,
  chartOptions: ChartOptions | null,
  params,
  editUrl: string | null = null,
): JSX.Element | null => {
  const EditWidgetAction = editUrl ? (
    <Link to={editUrl}>
      <FormattedMessage id="dashboard.edit_chart" />
    </Link>
  ) : null;

  const isNoResult = handleEmpty(chartOptions);

  if (params.isFetching) {
    return (
      <InfoWrapper>
        <Space direction="vertical">
          <Loading size="large" />
          <LoadingMessage>
            <FormattedMessage id="loading" />
          </LoadingMessage>
        </Space>
      </InfoWrapper>
    );
  }

  switch (status) {
    case 'loading':
      return (
        <InfoWrapper>
          <Space direction="vertical">
            <Loading size="large" />
            <LoadingMessage>
              <FormattedMessage id="loading" />
            </LoadingMessage>
          </Space>
        </InfoWrapper>
      );
    case 'error':
      return (
        <InfoWrapper>
          <ErrorState
            message={<FormattedMessage id="dashboard.widget.error" />}
            action={EditWidgetAction}
          />
        </InfoWrapper>
      );
    case 'success':
      return isNoResult ? (
        <Chart
          highcharts={Highcharts}
          options={chartOptions as Options}
          {...params}
          immutable
        />
      ) : (
        <InfoWrapper>
          <NoResults
            message={<FormattedMessage id="dashboard.widget.no_data" />}
            action={EditWidgetAction}
          />
        </InfoWrapper>
      );
    case 'idle':
    default:
      return null;
  }
};

export const ChartWidget: FC<{
  widget: WidgetT;
  dashboardFilter: Filter;
  dashboardSimpleFilter: SimpleSearchFilter;
}> = ({ widget, dashboardFilter, dashboardSimpleFilter }) => {
  const { widgetName, widgetId, widgetType, dashboardId } = widget;
  const {
    data: [{ response: chartOptions }] = [{ response: undefined }],
    status,
    isFetching,
  } = useResolveMultipleWidgetsQuery(
    dashboardId,
    [widgetId],
    dashboardSimpleFilter,
  );
  const getEditWidgetUrl = useGetEditWidgetUrlWithSearch();
  const { data: allFields, isLoading: fieldsLoading } = useAllFieldsQuery();
  const { projectFields, taskFields } = getProjectAndTaskFields(allFields);
  const objectType = (widget as CustomChartWidget)?.widgetObjectType;
  const [showLabels] = useLocalStorage(
    TOGGLE_CHART_LABEL_LOCALSTORAGE_KEY,
    false,
  );
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    scrollIntialiser(Highcharts);

    return () => {
      chartCleanup(Highcharts);
    };
  }, []);

  // unmounting and remounting chart to refresh it, need to find a better way to fix this
  useEffect(() => {
    setRefresh(true);
    const timerId = setTimeout(() => setRefresh(false), 200);

    return () => clearTimeout(timerId);
  }, [showLabels]);

  const fieldMap = useMemo(() => {
    return keyBy(allFields ?? [], 'fieldId');
  }, [allFields]);

  const field = getFieldInfo(
    chartOptions?.xAxis?.field,
    objectType,
    projectFields,
    taskFields,
  );

  const transformedOptions = transformOptions(
    chartOptions as ChartOptions,
    widgetType,
    (widget as CustomChartWidget)?.metaData,
    field,
    objectType,
    showLabels,
  );
  const isDateField =
    isNativeDateField((chartOptions as ChartOptions)?.xAxis?.field) ||
    field?.fieldDataType === FieldDataType.Date;

  const Menu = useWidgetMenu(widget, dashboardSimpleFilter);
  const editUrl = getEditWidgetUrl(dashboardId, widgetId);
  const filter = useWidgetFilter({
    widget,
    defaultGroup: FilterGroupType.Project,
  });

  const [isVisible, { setTrue: openModel, setFalse: closeModal }] =
    useBoolean(false);
  const [data, setData] = useState<Project[]>([]);
  const [selectedSeries, setSelectedSeries] =
    useState<Highcharts.SeriesOptionsType | string | null>(null);

  const { mutate, isLoading } = useDrillWidgetMutationV2(widgetId);
  const [drillDownFilter, setDrillDownFilter] =
    useState<SimpleSearchFilter | undefined>();

  const chartSeriesOptions =
    useMemo((): Highcharts.PlotSeriesOptions | null => {
      if (chartOptions) {
        const options = {
          cursor: 'pointer',
          point: {
            events: {
              click(event) {
                const {
                  point: {
                    category,
                    series: { name },
                  },
                } = event;

                const result = getFilterForDrillWidget(
                  transformedOptions,
                  event,
                );
                const clickedSeries = chartOptions?.series?.find(
                  (s) => name === s.name,
                );
                const customChartDrilldown =
                  result && widgetType === WidgetType.CustomChart;
                const defaultDrilldown = result && clickedSeries;

                if (customChartDrilldown || defaultDrilldown) {
                  if (WidgetType.TaskByStatus !== widgetType) {
                    openModel();
                  }
                  if (defaultDrilldown) {
                    setSelectedSeries(clickedSeries);
                  }
                  if (customChartDrilldown) {
                    setSelectedSeries(category);
                  }
                  setDrillDownFilter({
                    match: QueryCombinerType.All,
                    ...result,
                  });

                  if (
                    objectType === ObjectType.Project ||
                    ![WidgetType.TaskByStatus, WidgetType.CustomChart].includes(
                      widgetType,
                    )
                  ) {
                    mutate(
                      {
                        drillDownFilter: result,
                        dashboardFilter: dashboardSimpleFilter,
                      },
                      {
                        onSuccess: (response) => setData(response),
                      },
                    );
                  }
                }
              },
            },
          },
        };

        return options;
      }

      return null;
    }, [
      chartOptions,
      dashboardSimpleFilter,
      mutate,
      objectType,
      openModel,
      transformedOptions,
      widgetType,
    ]);

  const fieldNames = fieldsOfChart(chartOptions, fieldMap);

  const [defaultChartOptions, expandedChartOptions, sortType, setSortType] =
    useChartOptionsWithSortAndDrillDown(
      transformedOptions,
      widgetType,
      isDateField,
      chartSeriesOptions,
    );

  const ChartContent = getChartContent(
    status,
    defaultChartOptions,
    {
      zoomLevel: (widget as CustomChartWidget)?.metaData?.zoomLevel,
      sortType,
      filter: widget.simpleSearchFilter,
      isFetching: isFetching || refresh,
    },
    editUrl,
  );

  const ExpandedChartContent = getChartContent(
    status,
    expandedChartOptions,
    {
      zoomLevel: (widget as CustomChartWidget)?.metaData?.zoomLevel,
      sortType,
      filter: widget.simpleSearchFilter,
    },
    editUrl,
  );

  return (
    <ChartWidgetContent
      widgetId={widgetId}
      filter={filter}
      dashboardFilter={dashboardFilter}
      sortType={sortType}
      setSortType={setSortType}
      widgetName={widgetName}
      chartContent={ChartContent}
      chartMenu={Menu}
      loading={isLoading || fieldsLoading}
      expandedChartContent={ExpandedChartContent}
      selectedSeries={selectedSeries}
      listData={data}
      setData={setData}
      setSelectedSeries={setSelectedSeries}
      closeModal={closeModal}
      isModalVisible={isVisible}
      isDateField={isDateField}
      widgetType={widgetType}
      objectType={objectType}
      fieldNames={fieldNames}
      dashboardSimpleFilter={dashboardSimpleFilter}
      widgetSimpleFilter={drillDownFilter}
    />
  );
};
